import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, CircleCheck } from 'lucide-react';
import type { JourneyResource } from '../../../../types/journey';

interface Props {
  resources: JourneyResource[];
  acceptedKeys: Set<string>;
  acceptingKey: string | null;
  onAccept: (resourceKey: string) => void;
  /** Fuerza la apertura de este recurso (ej. al cerrar el popup de "nueva etapa"). */
  autoOpenKey?: string | null;
}

export const JourneyResourceAccordion: React.FC<Props> = ({ resources, acceptedKeys, acceptingKey, onAccept, autoOpenKey }) => {
  const [openKey, setOpenKey] = useState<string | null>(null);

  useEffect(() => {
    if (autoOpenKey) setOpenKey(autoOpenKey);
  }, [autoOpenKey]);

  return (
    <div className="rounded-xl border border-gray-200 overflow-hidden bg-white">
      {resources.map((resource) => {
        const isAccepted = acceptedKeys.has(resource.resource_key);
        const isOpen = openKey === resource.resource_key;
        const Icon = resource.icon;
        const isSubmitting = acceptingKey === resource.resource_key;

        return (
          <div key={resource.resource_key} id={`journey-resource-${resource.resource_key}`} className="border-b border-gray-200 last:border-b-0">
            <button
              type="button"
              onClick={() => setOpenKey((prev) => (prev === resource.resource_key ? null : resource.resource_key))}
              className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
            >
              <span className={`flex items-center justify-center w-8 h-8 rounded-lg shrink-0 ${resource.iconBgClassName}`}>
                <Icon size={15} className={resource.iconColorClassName} />
              </span>
              <span className="flex-1 min-w-0">
                <span className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900">{resource.title}</span>
                  {resource.isNewInStage && !isAccepted && (
                    <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700 shrink-0">
                      Nuevo
                    </span>
                  )}
                </span>
                <span className="block text-xs text-gray-400 mt-0.5">{resource.subtitle}</span>
              </span>
              <span className="flex items-center gap-1.5 shrink-0">
                {isAccepted && <CircleCheck size={16} className="text-green-600" />}
                {isOpen ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
              </span>
            </button>

            {isOpen && (
              <div className="px-4 pb-4 pl-[52px]">
                <div className="text-sm text-gray-600 leading-[1.7]">
                  {resource.body}
                </div>
                <div className="mt-3 pt-3 border-t border-gray-100">
                  {isAccepted ? (
                    <span className="flex items-center gap-1.5 text-sm font-medium text-green-600">
                      <CircleCheck size={15} />
                      Aceptado
                    </span>
                  ) : (
                    <label className="flex items-start gap-2.5 text-sm font-medium text-gray-800 bg-violet-50 border border-violet-200 rounded-lg px-3 py-2.5 cursor-pointer hover:bg-violet-100 transition-colors">
                      <input
                        type="checkbox"
                        className="mt-0.5 w-4 h-4 accent-violet-600 shrink-0"
                        checked={false}
                        disabled={isSubmitting}
                        onChange={(e) => {
                          if (e.target.checked) onAccept(resource.resource_key);
                        }}
                      />
                      <span>{resource.checkbox_label}</span>
                    </label>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default JourneyResourceAccordion;
